import { useState } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import I18n from "i18n-js";
import { getAuth, sendEmailVerification } from "firebase/auth";
import { useDialog } from "app/hook";
import { RootState } from "app/store";
import { DialogType } from "app/types";
import { parentsFirebase } from "app/Auth";
import { ConfirmMessageBox } from "common/elements";
import { btnDelay, usePlaySound } from "common/utils";
import { RouteProps } from "../type";
import { GoBackBtn } from "../GoBackBtn";
import { ParentsUserProfile } from "./ParentsUserProfile";
import { ParentsUserAccountLink } from "./ParentsUserAccountLink";

export const ParentsUserAccount = (props: RouteProps) => {
  const { navigate } = props;
  const play = usePlaySound();
  const handleDialog = useDialog();
  const handleGoBack = () => navigate("/");
  const parentsAuth = getAuth(parentsFirebase);
  const [signOut, setSignOut] = useState(false);
  const parent = useSelector((state: RootState) => state.user.parent);

  const handleSendSignInLinkToEmail = () => {
    const actionCodeSettings = {
      url: import.meta.env.VITE_PUBLIC_URL,
      handleCodeInApp: false,
    };
    handleDialog({ type: DialogType.LOADING, value: true });
    sendEmailVerification(parentsAuth.currentUser, actionCodeSettings)
      .then(() =>
        toast(
          <div>
            <p className="text text-textcolor-black">
              {I18n.t("MSG_SIGN_IN_FORGOT_PASSWORD_SUCCESS_CONTENT")}
            </p>
          </div>,
        ),
      )
      .catch((error) => {
        console.error(error);
      })
      .finally(() => handleDialog({ type: DialogType.LOADING, value: false }));
  };

  const handleOpenSignOut = () => {
    play();
    btnDelay(() => setSignOut(true));
  };
  const handleSignOut = async () => {
    play();
    btnDelay(async () => {
      try {
        await parentsAuth.signOut();
        handleDialog({ type: DialogType.PARENT_GATE, value: false });
      } catch (error) {
        console.error(error);
        handleDialog({ type: DialogType.ERROR_NETWORK, value: true });
      }
    });
  };

  const handleCloseDialog = () => {
    play();
    btnDelay(() => {
      setSignOut(false);
    });
  };

  if (!parent) {
    return null;
  }

  return (
    <div className="flex-col-center w-full h-full py-4 pb-0">
      {signOut && (
        <ConfirmMessageBox
          title={I18n.t("MSG_SETTING_PARENTS_USER_SIGN_OUT_TITLE")}
          message={I18n.t("MSG_SETTING_PARENTS_USER_SIGN_OUT_MESSAGE")}
          close={handleCloseDialog}
          confirm={handleSignOut}
          is_parents
        />
      )}

      <div className="flex-col-el w-full flex-1 overflow-hidden gap-10">
        <div className="flex-col-el items-center justify-start visible-scrollbar">
          <div className="flex-col-el gap-8">
            <ParentsUserProfile user={parent} />

            <div className="flex-row-el gap-2 ml-32">
              <div className="flex-row-el w-[250px]">
                <p className="text-body text-left text-textcolor-black text-font-size-body2">
                  {I18n.t("MSG_SETTING_PARENTS_USER_PROFILE_EMAIL")}
                </p>
              </div>

              <div className="flex-row-el w-[400px]">
                <p className="text-body text-left text-textcolor-black text-font-size-body2">
                  {parent.email}
                </p>
              </div>
            </div>

            <div className="flex-row-el gap-2 ml-32">
              <div className="flex-row-el items-center w-[250px]">
                <p className="text-body text-left text-textcolor-black text-font-size-body2">
                  {I18n.t("MSG_SETTING_PARENTS_USER_PROFILE_EMAIL_VERIFIED")}
                </p>
              </div>

              {parent.provider !== "password" ||
              parentsAuth.currentUser?.emailVerified ? (
                <div className="flex-row-el w-[180px]">
                  <p className="text-body text-left text-textcolor-black text-font-size-body2">
                    {I18n.t(
                      "MSG_SETTING_PARENTS_USER_PROFILE_EMAIL_VERIFIED_DONE",
                    )}
                  </p>
                </div>
              ) : (
                <div className="flex-col-el flex-center relative upsilon w-[280px]">
                  <button
                    onClick={handleSendSignInLinkToEmail}
                    className="btn btn-primary w-full"
                  >
                    <p className="text-body text-[24px] text-white !mb-[3px]">
                      {I18n.t(
                        "MSG_SETTING_PARENTS_USER_PROFILE_EMAIL_VERIFIED_NOT_YET",
                      )}
                    </p>
                  </button>
                </div>
              )}
            </div>

            {parent.provider === "password" && (
              <div className="flex-row-el gap-2">
                <div className="flex-row-el items-center w-[250px]">
                  <p className="text-body text-left text-textcolor-black text-font-size-body2 whitespace-pre">
                    {I18n.t("MSG_SETTING_PARENTS_USER_PROFILE_ACCOUNT")}
                  </p>
                </div>

                <ParentsUserAccountLink />
              </div>
            )}

            <div className="flex-row-el gap-2 w-full ml-32">
              <div className="flex-row-el w-[250px]">
                <p className="text-body text-left text-textcolor-black text-font-size-body2">
                  {I18n.t("MSG_SETTING_PARENTS_USER_PROFILE_CREATED_AT")}
                </p>
              </div>

              <p className="text-body text-left text-textcolor-black text-font-size-body2">
                {dayjs(parent.created_at).format("YYYY-MM-DD")}
              </p>
            </div>

            <div className="flex-col-el w-full gap-8 items-center">
              <div className="flex-col-el gap-5 w-[60%]">
                {parent.provider === "password" && (
                  <div className="flex-row-center w-full upsilon">
                    <button
                      onClick={() => navigate("/changePassword")}
                      className="btn btn-blue w-full"
                    >
                      <p className="text-body text-[24px] text-white !mb-[3px]">
                        {I18n.t(
                          "MSG_SETTING_PARENTS_USER_PROFILE_GOTO_CHANGE_PASSWORD_BTN",
                        )}
                      </p>
                    </button>
                  </div>
                )}

                <div className="flex-row-center w-full upsilon">
                  <button
                    onClick={handleOpenSignOut}
                    className="btn btn-danger w-full"
                  >
                    <p className="text-body text-[24px] text-white !mb-[3px]">
                      {I18n.t("MSG_SETTING_PARENTS_USER_PROFILE_SIGN_OUT_BTN")}
                    </p>
                  </button>
                </div>

                <div className="flex-row-center w-full mb-10">
                  <p className="text-body text-font-size-body2 text-textcolor-black text-center">
                    登録したアカウントの削除をご希望の場合は
                    <span
                      onClick={() => {
                        play();
                        btnDelay(() => navigate("/parentsUserUnsubscribe"));
                      }}
                      className="text-blue-600 underline hover:text-blue-800 cursor-pointer"
                    >
                      こちら
                    </span>
                    からお願いします。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-row-center my-2">
        <GoBackBtn handleGoBack={handleGoBack} />
      </div>
    </div>
  );
};
